'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

interface Story {
  id: number;
  category: string;
  num: string;
  image: string;
  imageAlt: string;
  title: string;
  description: string;
  link: string;
  linkLabel: string;
  isNew?: boolean;
}

const stories: Story[] = [
{
  id: 1,
  category: '/ Flagship Project',
  num: '01 / 04',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b34607e9-1778365791235.png",
  imageAlt: 'Uganda Oil Refinery construction site at Kabaale Hoima District downstream processing facility',
  title: 'Uganda Refinery Progresses on Schedule',
  description: 'Construction of strategic storage and infrastructure advances at the Hoima refinery site, anchoring national energy security.',
  link: '/projects/refinery',
  linkLabel: 'Explore the project'
},
{
  id: 2,
  category: '/ Strategic Partnership',
  num: '02 / 04',
  image: 'https://www.unoc.co.ug/wp-content/uploads/2025/10/UNOC-BakerHughes.jpg',
  imageAlt: 'UNOC and Baker Hughes EHO Ltd signing MoU for technical capabilities and Center of Excellence partnership',
  title: 'UNOC Strengthens Regional Energy Security',
  description: 'Strategic collaborations with regional partners to enhance energy infrastructure and security across East Africa.',
  link: '/news/unoc-strengthens-regional-energy-security',
  linkLabel: 'Read in the newsroom'
},
{
  id: 3,
  category: '/ Milestone',
  num: '03 / 04',
  image: 'https://www.unoc.co.ug/wp-content/uploads/2025/12/WhatsApp-Image-2025-12-20-at-2.15.50-PM.jpeg',
  imageAlt: 'UNOC 10th anniversary celebration event with staff and government officials Uganda petroleum sector',
  title: 'Celebrating 10 Years of UNOC',
  description: 'A decade of building Uganda\'s oil and gas sector — honoring the people and partners who made it possible.',
  link: '/news/unoc-celebrated-10-years-petroleum-sector',
  linkLabel: 'Read in the newsroom'
},
{
  id: 4,
  category: '/ Technical Capacity',
  num: '04 / 04',
  image: 'https://www.unoc.co.ug/wp-content/uploads/2025/10/UNOC-BakerHughes.jpg',
  imageAlt: 'UNOC and Baker Hughes representatives at MoU signing ceremony for technical capacity building petroleum',
  title: 'UNOC and Baker Hughes Sign MoU',
  description: 'Partnership agreement to enhance technical capabilities and knowledge transfer in petroleum operations and management.',
  link: '/news/unoc-baker-hughes-mou-technical-capacity',
  linkLabel: 'Read in the newsroom',
  isNew: true
}];


export default function FeaturedStories() {
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setVisible(true);},
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const prev = () => setCurrent((c) => (c - 1 + stories.length) % stories.length);
  const next = () => setCurrent((c) => (c + 1) % stories.length);
  const story = stories[current];

  return (
    <section
      ref={sectionRef}
      aria-label="Featured highlights"
      className="w-full bg-white py-24">
      
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <div
          className="mb-12 flex flex-wrap items-end justify-between gap-6"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'none' : 'translateY(20px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease'
          }}>
          
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 02 ]</span>
              <div className="h-[1px] w-12 bg-[#CC0000]" />
              <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em]" style={{ fontFamily: 'Inter, sans-serif' }}>Highlights</span>
            </div>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F0F0F] leading-[1.05] tracking-tight"
              style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.025em' }}>
              
              Featured <span className="text-[#CC0000]">Stories</span>
            </h2>
          </div>
          <div className="flex gap-2">
            <button
              aria-label="Previous slide"
              onClick={prev}
              className="h-12 w-12 border border-gray-200 text-[#0F0F0F] hover:bg-[#CC0000] hover:text-white hover:border-[#CC0000] transition-colors flex items-center justify-center rounded-sm">
              
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <button
              aria-label="Next slide"
              onClick={next}
              className="h-12 w-12 border border-gray-200 text-[#0F0F0F] hover:bg-[#CC0000] hover:text-white hover:border-[#CC0000] transition-colors flex items-center justify-center rounded-sm">
              
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>
        </div>

        {/* Story card */}
        <div
          className="relative grid lg:grid-cols-2 gap-0 border border-gray-200"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'none' : 'translateY(20px)',
            transition: 'opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s'
          }}>
          
          {/* Image */}
          <div className="relative aspect-[4/3] lg:aspect-auto overflow-hidden bg-gray-100">
            <img
              src={story.image}
              alt={story.imageAlt}
              className="w-full h-full object-cover"
              style={{ transition: 'opacity 0.5s ease' }} />
            
            <div className="absolute inset-0 bg-[#0F0F0F]/10" />
            <span className="absolute top-4 right-4 font-mono text-[10px] text-white bg-black/40 backdrop-blur-sm px-2 py-1 tracking-[0.15em] rounded-sm">
              {story.num}
            </span>
            {story.isNew &&
            <span className="absolute top-4 left-4 bg-[#CC0000] text-white text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-sm">
                New
              </span>
            }
          </div>

          {/* Content */}
          <div className="p-8 lg:p-12 flex flex-col justify-between bg-white">
            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#CC0000] mb-4">
                {story.category}
              </p>
              <h3
                className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0F0F0F] leading-[1.1] mb-5"
                style={{ fontFamily: 'Inter, sans-serif' }}>
                
                {story.title}
              </h3>
              <p className="text-base text-gray-600 leading-relaxed mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
                {story.description}
              </p>
            </div>
            <div className="flex items-center justify-between">
              <Link
                href={story.link}
                className="inline-flex items-center gap-2 bg-[#CC0000] text-white pl-5 pr-2 py-2.5 rounded-sm text-sm font-semibold hover:bg-[#AA0000] transition-colors group">
                
                {story.linkLabel}
                <span className="bg-white/15 group-hover:bg-white/25 transition-colors p-1.5 rounded-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M7 7h10v10" />
                    <path d="M7 17 17 7" />
                  </svg>
                </span>
              </Link>
              {/* Dots */}
              <div className="flex gap-2">
                {stories.map((_, i) =>
                <button
                  key={i}
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => setCurrent(i)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                  i === current ? 'bg-[#CC0000]' : 'bg-gray-300 hover:bg-gray-400'}`
                  } />

                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}